import { useContext, useEffect, type ReactNode } from 'react';
import { drawElements } from '../../elements/layout_elements.ts';
import { isSwitchedOn, switchedReadings } from '../../values/on_off.ts';
import { CanvasView } from '../../view/canvas_view.tsx';
import { useLegacyIcon } from '../../view/icons.ts';
import { useTapCommand } from '../../view/tap_command.ts';
import { useTileEnvironment } from '../../view/tile_environment.ts';
import { TileStyleOverrideContext } from '../../view/tile_style.ts';
import { switchTileElements, switchTileLook, type SwitchTile } from './switch_tile.ts';

/** The content area of a `switch` tile: the icon of its state; a tap switches it. */
export function SwitchTileView({ tile }: { tile: SwitchTile }) : ReactNode {
    const readings = useTileEnvironment().useReadings(tile, tile.options.source);
    const tap = useTapCommand(tile, tile.options.source);
    const icons = { on: useLegacyIcon(tile.options.onIcon), off: useLegacyIcon(tile.options.offIcon) };
    const on = isSwitchedOn(readings);
    const look = switchTileLook(tile, on, icons);

    // The state's tile style over the tile's own (SW-1), as the status tile's.
    const setOverride = useContext(TileStyleOverrideContext);
    const styleKey = JSON.stringify(look.style ?? null);
    useEffect(() => {
        setOverride(styleKey === 'null' ? undefined : (JSON.parse(styleKey) as typeof look.style));
    }, [styleKey, setOverride]);
    useEffect(() => () => setOverride(undefined), [setOverride]);

    const toggle = () : void => {
        const next = on !== true;
        void tap(next ? 'on' : 'off', undefined, switchedReadings(readings, next));
    };

    return (
        <div style={{ position: 'absolute', inset: 0, cursor: 'pointer' }} onClick={toggle}>
            <CanvasView deps={[look.icon, look.color]} draw={(ctx, width, height) => drawElements(ctx, { left: 0, top: 0, width, height }, switchTileElements(look))} />
        </div>
    );
}
